import { computed, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CdkDragStart } from "@angular/cdk/drag-drop";
import { IBaseCard, isJobsBillingDashboard } from "@app/views-interfaces";
import { ITaxReturnStatus } from "@app/views-interfaces/tax-return-status-model.interface";
import { isJobsBillingCard } from "@app/views-interfaces/jobs-billing-card.interface";
import { ApplicationType } from "@app/views-interfaces/ApplicationType.enum";
import { getCardStatusId } from "@app/views-interfaces/card-utils";
import { DragAndDropService } from "@app/drag-and-drop";
import { AuthService } from "../auth";
import { CardViewService } from "../card-view-service/card-view.service";
import { DashboardsService } from "../dashboards-service";
import { JobsAndBillingCommonService } from "../jobs-and-billing-common-service/jobs-and-billing-common.service";
import { MappingOfStatusesService } from "../mapping-of-statuses-service/mapping-of-statuses.service";
import { isUnassigned } from "../users-service/users.service";
import { ICardDragAndDropStrategy, CardState } from "./card-drag-and-drop-strategy.interface";
import { JobsBillingDragAndDropStrategy } from "./jobs-billing-drag-and-drop.strategy";
import { T1DragAndDropStrategy } from "./t1-drag-and-drop.strategy";

@Injectable({ providedIn: "root" })
export class CardDragAndDropService {
    private readonly authService = inject(AuthService);
    private readonly cardViewService = inject(CardViewService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly dragAndDropService = inject(DragAndDropService);
    private readonly jobsAndBillingCommonService = inject(JobsAndBillingCommonService);
    private readonly jobsBillingStrategy = inject(JobsBillingDragAndDropStrategy);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly t1Strategy = inject(T1DragAndDropStrategy);

    private readonly activeStrategy = computed<ICardDragAndDropStrategy>(() =>
        isJobsBillingDashboard(this.dashboardsService.currentDashboard()) ? this.jobsBillingStrategy : this.t1Strategy,
    );

    readonly dragOriginalCardId = signal<string>("");
    readonly dragOriginalStatusIds = signal<Set<string>>(new Set<string>());
    readonly dragOriginalUserId = signal<string | null>("");
    readonly dragOriginalJobTypeId = signal<number | null>(null);

    readonly availableJobStatusIdsByJobType = this.jobsBillingStrategy.availableJobStatusIdsByJobType;

    private readonly isJobsBillingDashboard = computed(() => isJobsBillingDashboard(this.dashboardsService.currentDashboard()));

    readonly availableJobStatusIds = computed<Set<string>>(() => {
        const jobTypeId = this.dragOriginalJobTypeId();
        if (jobTypeId === null) return new Set<string>();
        return this.availableJobStatusIdsByJobType().get(jobTypeId) ?? new Set<string>();
    });

    constructor() {
        this.dragAndDropService.dragStarted.pipe(takeUntilDestroyed()).subscribe((event: CdkDragStart<IBaseCard>) => {
            const card = event.source.data;
            const strategy = this.activeStrategy();
            this.dragOriginalCardId.set(card.cardId);
            this.dragOriginalStatusIds.set(strategy.captureOriginalStatusIds(card));
            this.dragOriginalJobTypeId.set(isJobsBillingCard(card) ? (card.job?.typeId ?? null) : null);
            this.dragOriginalUserId.set(strategy.captureOriginalUserId(card));
        });

        this.dragAndDropService.dragEnded.pipe(takeUntilDestroyed()).subscribe(() => {
            this.dragOriginalCardId.set("");
            this.dragOriginalStatusIds.set(new Set<string>());
            this.dragOriginalUserId.set("");
            this.dragOriginalJobTypeId.set(null);
        });
    }

    onDrop(card: IBaseCard, statusId: string, targetUserId: string = ""): void {
        if (this.cardViewService.currentViewDisplayMode === "details") {
            this.onAssigneeDrop(card, statusId, targetUserId);
        } else {
            this.onStatusDrop(card, statusId);
        }
    }

    private async onStatusDrop(card: IBaseCard, statusId: string): Promise<void> {
        const strategy = this.activeStrategy();
        if (strategy.isValidStatusChange(card, statusId)) {
            await strategy.onStatusDrop(card, statusId);
        }
    }

    private onAssigneeDrop(card: IBaseCard, statusId: string, targetUserId: string): void {
        const strategy = this.activeStrategy();
        if (strategy.isValidAssigneeChange(card, targetUserId, statusId)) {
            strategy.onAssigneeDrop(card, targetUserId, statusId);
        }
    }

    getFirstValidStatusOf(state: CardState): ITaxReturnStatus | null {
        return this.activeStrategy().getFirstValidStatusOf(state, this.dragOriginalJobTypeId());
    }

    getDisabledDropListIds(statusIds: string[], assignedUserId: string | null, columnActive: boolean): Set<string> {
        if (this.cardViewService.currentViewDisplayMode === "details") {
            return this.getDisabledAssigneeDropListIds(statusIds, assignedUserId, columnActive);
        }
        return this.getDisabledStatusDropListIds(statusIds);
    }

    getDisabledStatusDropListIds(statusIds: string[]): Set<string> {
        const originalStatusIds = this.dragOriginalStatusIds();
        if (this.isJobsBillingDashboard()) {
            const availableJobStatusIds = this.availableJobStatusIds();
            return new Set(statusIds.filter((id) => !availableJobStatusIds.has(id) || originalStatusIds.has(id)));
        }
        return originalStatusIds?.size === 1 ? new Set(originalStatusIds) : new Set<string>();
    }

    private getDisabledAssigneeDropListIds(statusIds: string[], assignedUserId: string | null, columnActive: boolean): Set<string> {
        if (!this.dragAndDropService.isDragging()) return new Set<string>();
        const originalUserId = this.dragOriginalUserId();
        const isDisabled = (isUnassigned(assignedUserId) && isUnassigned(originalUserId)) || assignedUserId === originalUserId || !columnActive;
        if (!isDisabled) return new Set<string>();
        return new Set(statusIds);
    }

    getDisabledDragCardIds(cards: IBaseCard[]): Set<string> {
        const isDetailsDisplayMode = this.cardViewService.currentViewDisplayMode === "details";
        const jobsBillingAccessKey = isDetailsDisplayMode ? "currently_responsible" : "drag_drop";

        if (this.isJobsBillingDashboard() && !this.jobsAndBillingCommonService.hasAccess(jobsBillingAccessKey)) {
            return new Set<string>(cards.map((card) => card.cardId));
        }

        const appType = this.dashboardsService.currentDashboard()?.ApplicationType;
        const isIFirmTaxT1DetailsView = appType === ApplicationType.iFirmTaxT1 && isDetailsDisplayMode;
        const canUserEditAssignee = this.authService.currentUser?.can_edit_assignee === "True";

        if (!isIFirmTaxT1DetailsView || canUserEditAssignee) {
            return new Set<string>();
        }

        const managerOrPartnerStatusIds = new Set(
            this.mappingOfStatusesService
                .getAllReturnStatuses()
                .filter((status) => status.Role?.Id === "1" || status.Role?.Id === "2")
                .map((status) => status.TaxReturnStatus.Id)
                .filter((id): id is string => !!id),
        );

        return new Set(cards.filter((card) => managerOrPartnerStatusIds.has(getCardStatusId(card))).map((card) => card.cardId));
    }
}
